import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useDynamicColors from "../../../../../hooks/useDynamicColors";
import useClientName from "../../../../../hooks/useClientName";
import "./style.css";

export const CorporativoLoginDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientName = useClientName();
  const colors = useDynamicColors();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = () => {
    const currentParams = location.search;
    navigate(`/backoffice-users-${clientName}-web${currentParams}`);
  };

  return (
    <div style={styles.laptop}>
      <div style={styles.screen}>
        <div style={styles.container}>
          {/* Left panel - decorative gradient */}
          <div
            style={{
              ...styles.leftPanel,
              background: `linear-gradient(160deg, ${colors.primario} 0%, ${colors.quinto} 100%)`,
            }}
          >
            {/* Large sphere - top right */}
            <div
              style={{
                position: "absolute",
                right: "-10%",
                top: "-15%",
                width: "250px",
                height: "250px",
                borderRadius: "50%",
                opacity: 0.7,
                background: `radial-gradient(circle at 40% 40%, ${colors.terciario}, ${colors.primario}00)`,
              }}
            />
            {/* Medium sphere - center left */}
            <div
              style={{
                position: "absolute",
                left: "5%",
                top: "30%",
                width: "180px",
                height: "180px",
                borderRadius: "50%",
                opacity: 0.6,
                background: `radial-gradient(circle at 60% 35%, ${colors.secundario}cc, ${colors.quinto}00)`,
              }}
            />
            {/* Small sphere - bottom center */}
            <div
              style={{
                position: "absolute",
                bottom: "10%",
                left: "35%",
                width: "130px",
                height: "130px",
                borderRadius: "50%",
                opacity: 0.5,
                background: `radial-gradient(circle at 50% 40%, ${colors.septimo}aa, ${colors.primario}00)`,
              }}
            />
            {/* Accent ring - top left */}
            <div
              style={{
                position: "absolute",
                left: "15%",
                top: "8%",
                width: "90px",
                height: "90px",
                borderRadius: "50%",
                border: `2px solid ${colors.cuaternario}`,
                opacity: 0.2,
              }}
            />
            {/* Accent ring - bottom right */}
            <div
              style={{
                position: "absolute",
                bottom: "25%",
                right: "10%",
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                border: `2px solid ${colors.sexto}`,
                opacity: 0.15,
              }}
            />
            {/* Tagline text */}
            <div
              style={{
                position: "relative",
                zIndex: 10,
                display: "flex",
                alignItems: "flex-end",
                padding: "24px",
                height: "100%",
              }}
            >
              <h1
                style={{
                  maxWidth: "220px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: "white",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Your trusted digital banking platform
              </h1>
            </div>
          </div>

          {/* Right panel - login form */}
          <div style={styles.rightPanel}>
            {/* Language flags top-right */}
            <div style={styles.flagsRow}>
              <img
                src="https://webfly-j.presales01.veritran.cloud/api/resources/4720/bandera_us"
                height={18}
                width={18}
                alt="EN"
                style={{ borderRadius: "50%" }}
              />
              <img
                src="/img/espana.png"
                height={18}
                width={18}
                alt="ES"
                style={{ borderRadius: "50%" }}
              />
              <img
                src="/img/portugal.png"
                height={18}
                width={18}
                alt="PT"
                style={{ borderRadius: "50%" }}
              />
            </div>

            {/* Centered form area */}
            <div style={styles.formWrapper}>
              {/* Logo */}
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <img
                  src={
                    colors.url_logo ||
                    "https://vxapp.wfsystems.com.ar/storage/logos/20240416192440.png"
                  }
                  alt="Logo"
                  style={{
                    height: "36px",
                    objectFit: "contain",
                  }}
                />
              </div>

              {/* Welcome text */}
              <h2
                style={{
                  textAlign: "center",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "#111827",
                  marginBottom: "4px",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Welcome
              </h2>
              <p
                style={{
                  textAlign: "center",
                  fontSize: "0.75rem",
                  color: "#6B7280",
                  marginBottom: "20px",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Enter your credentials to continue
              </p>

              {/* Username field */}
              <div style={{ marginBottom: "12px" }}>
                <label style={styles.label}>Username</label>
                <div style={styles.inputWrapper}>
                  <div style={styles.inputIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Password field */}
              <div style={{ marginBottom: "16px" }}>
                <label style={styles.label}>Password</label>
                <div style={styles.inputWrapper}>
                  <div style={styles.inputIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    style={styles.input}
                  />
                  <div
                    style={styles.eyeToggle}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>

              {/* Log In button */}
              <button
                onClick={handleLogin}
                className="highlighted-button"
                style={{
                  ...styles.loginButton,
                  backgroundColor: colors.primario,
                }}
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      </div>
      <div style={styles.base}></div>
    </div>
  );
};

const styles = {
  laptop: {
    marginTop: "30px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "#333",
    borderRadius: "15px 15px 5px 5px",
    padding: "10px",
    width: "960px",
    position: "relative",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
  },
  screen: {
    backgroundColor: "white",
    borderRadius: "5px",
    overflow: "hidden",
    width: "100%",
    height: "500px",
  },
  container: {
    display: "flex",
    height: "100%",
    fontFamily: "Inter, sans-serif",
  },
  leftPanel: {
    position: "relative",
    overflow: "hidden",
    width: "50%",
  },
  rightPanel: {
    width: "50%",
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  flagsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    padding: "12px 16px",
  },
  formWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0 48px",
  },
  label: {
    display: "block",
    fontSize: "0.7rem",
    fontWeight: 500,
    color: "#374151",
    marginBottom: "4px",
    fontFamily: "Inter, sans-serif",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: "10px",
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    padding: "8px 34px",
    border: "1px solid #D1D5DB",
    borderRadius: "8px",
    fontSize: "0.75rem",
    fontFamily: "Inter, sans-serif",
    outline: "none",
    boxSizing: "border-box",
  },
  eyeToggle: {
    position: "absolute",
    right: "10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
  },
  loginButton: {
    width: "100%",
    padding: "10px",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.8rem",
    fontWeight: 600,
    fontFamily: "Inter, sans-serif",
    cursor: "pointer",
  },
  base: {
    position: "absolute",
    width: "1100px",
    height: "20px",
    backgroundColor: "rgb(170, 170, 170)",
    borderRadius: "0px 0px 10px 10px",
    bottom: 0,
  },
};
